/**
 * micPrimerController
 *
 * Lets any native code path say "make sure the microphone has been explained and
 * asked for, then tell me the outcome" without importing React state or knowing
 * where the primer is mounted.
 *
 * `NativeMicPrimerGate` registers itself once, near the root of the app. Callers
 * await `ensureMicPrimed()` immediately before their first `getUserMedia`.
 *
 * On the web `ensureMicPrimed()` resolves instantly with 'web', so a caller can
 * await it unconditionally and the browser flow is untouched.
 */

import { isNative } from '@/lib/platform';
import { MIC_STATE, getRememberedMicState, shouldShowMicPrimer } from './micPermission';

/** Extra outcomes on top of MIC_STATE. */
export const PRIMER_RESULT = {
  WEB: 'web',
  DISMISSED: 'dismissed',
  NO_UI: 'no-ui',
};

let opener = null;

/**
 * Called by the mounted gate component.
 * @param {() => Promise<string>} fn
 * @returns {() => void} unregister
 */
export function registerMicPrimer(fn) {
  opener = fn;
  return () => {
    if (opener === fn) opener = null;
  };
}

/**
 * @returns {Promise<string>} one of MIC_STATE.* or PRIMER_RESULT.*
 */
export async function ensureMicPrimed() {
  if (!isNative()) return PRIMER_RESULT.WEB;
  if (!shouldShowMicPrimer()) return getRememberedMicState();
  if (!opener) return PRIMER_RESULT.NO_UI;
  try {
    return await opener();
  } catch {
    return MIC_STATE.ERROR;
  }
}
